import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Loader2, Home, Sparkles, Camera, Gem } from 'lucide-react';
import { TransformationPreset } from '../../services/job';
import { jobService } from '../../services/job';

// Size presets for Product Photo Studio
const SIZE_PRESETS: Record<string, { width: number; height: number }> = {
  reels: { width: 1080, height: 1920 },
  posts: { width: 1080, height: 1080 },
  marketplace: { width: 1200, height: 1200 }
};

interface PresetInputDialogProps {
  preset: TransformationPreset;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (transformations: any[]) => void;
}

interface EnvironmentPreset {
  id: string;
  category: string;
  name: string;
  description: string;
  prompt: string | null;
}

interface GroupedPresets {
  [category: string]: EnvironmentPreset[];
}

export function PresetInputDialog({ preset, isOpen, onClose, onConfirm }: PresetInputDialogProps) {
  const [inputValues, setInputValues] = useState<Record<string, string>>({});
  const [environmentPresets, setEnvironmentPresets] = useState<GroupedPresets>({});
  const [isLoadingPresets, setIsLoadingPresets] = useState(false);
  const [selectedEnvironment, setSelectedEnvironment] = useState<string>('');
  const [customEnvironment, setCustomEnvironment] = useState<string>('');

  // Check if a field should be visible based on its condition
  const shouldShowField = (field: any): boolean => {
    if (!field.condition) return true;
    return inputValues[field.condition.field] === field.condition.value;
  };

  useEffect(() => {
    if (isOpen && preset.inputFields) {
      // Initialize input values
      const initialValues: Record<string, string> = {};
      preset.inputFields.forEach(field => {
        // Check if we have a pre-populated value from the transformation
        const transformation = preset.transformations[0];
        if (transformation && (transformation.type === 'object_placement' || transformation.type === 'object_placement_pro')) {
          if (field.name === 'product' && transformation.product) {
            initialValues[field.name] = transformation.product;
          } else if (field.name === 'environment' && transformation.environment) {
            initialValues[field.name] = transformation.environment;
          } else {
            initialValues[field.name] = '';
          }
        } else if (field.type === 'color') {
          // Default color values
          initialValues[field.name] = field.defaultValue || field.placeholder || '#FFFFFF';
        } else if (field.type === 'checkbox') {
          // Use field defaultValue if provided, otherwise false
          initialValues[field.name] = field.defaultValue || 'false';
        } else {
          // Use field defaultValue if provided
          initialValues[field.name] = field.defaultValue || '';
        }
      });
      setInputValues(initialValues);

      // Load environment presets if needed
      const hasEnvironmentField = preset.inputFields.some(field => field.options === 'environmentPresets');
      if (hasEnvironmentField) {
        loadEnvironmentPresets();
      }
    }
  }, [isOpen, preset]);

  // Auto-populate width/height when size preset changes
  useEffect(() => {
    const outputSize = inputValues.outputSize;
    if (outputSize && SIZE_PRESETS[outputSize]) {
      const preset = SIZE_PRESETS[outputSize];
      setInputValues(prev => ({
        ...prev,
        customWidth: preset.width.toString(),
        customHeight: preset.height.toString()
      }));
    }
  }, [inputValues.outputSize]);

  const loadEnvironmentPresets = async () => {
    setIsLoadingPresets(true);
    try {
      const response = await jobService.getEnvironmentPresets();
      console.log('Environment presets response:', response);
      setEnvironmentPresets(response.presets || response);
    } catch (error) {
      console.error('Failed to load environment presets:', error);
    } finally {
      setIsLoadingPresets(false);
    }
  };

  const handleInputChange = (fieldName: string, value: string) => {
    setInputValues(prev => ({
      ...prev,
      [fieldName]: value
    }));

    // Handle environment selection
    if (fieldName === 'environment') {
      setSelectedEnvironment(value);
      if (value === 'custom') {
        setCustomEnvironment('');
      }
    }
  };

  const handleConfirm = () => {
    // Validate required fields (only visible fields)
    const missingFields = preset.inputFields?.filter(field =>
      field.required && shouldShowField(field) && !inputValues[field.name]
    ) || [];

    if (missingFields.length > 0) {
      // In a real app, show validation errors
      return;
    }

    // Apply input values to transformations
    const updatedTransformations = preset.transformations.map(transformation => {
      if (transformation.type === 'object_placement' || transformation.type === 'object_placement_pro') {
        // Get the actual environment value
        let environmentValue = inputValues.environment;

        if (selectedEnvironment === 'custom') {
          environmentValue = customEnvironment;
        } else if (selectedEnvironment && selectedEnvironment !== '') {
          // Find the selected preset
          const presetData = Object.values(environmentPresets)
            .flat()
            .find(p => p.id === selectedEnvironment);

          if (presetData && presetData.prompt) {
            environmentValue = presetData.prompt;
          }
        }

        return {
          ...transformation,
          product: inputValues.product || transformation.product,
          environment: environmentValue || transformation.environment,
          focus: inputValues.focus || 'hero',
          lighting: inputValues.lighting || 'bright_day'
        };
      }

      // Handle Product Photo Studio / Jewelry Photo Studio transformation
      if (transformation.type === 'product_photo_studio' || transformation.type === 'jewelry_photo_studio') {
        const outputSize = inputValues.outputSize || 'marketplace';
        const sizePreset = SIZE_PRESETS[outputSize];

        return {
          ...transformation,
          outputSize,
          customWidth: outputSize === 'custom'
            ? parseInt(inputValues.customWidth) || 1200
            : sizePreset?.width || 1200,
          customHeight: outputSize === 'custom'
            ? parseInt(inputValues.customHeight) || 1200
            : sizePreset?.height || 1200,
          backgroundColor: inputValues.backgroundColor || '#FFFFFF',
          colorSensitiveMode: inputValues.colorSensitiveMode === 'true',
          keepOriginalFraming: inputValues.keepOriginalFraming === 'true'
        };
      }

      return transformation;
    });

    onConfirm(updatedTransformations);
  };

  if (!preset.inputFields || preset.inputFields.length === 0) {
    return null;
  }

  // Determine dialog icon based on preset type
  const getDialogIcon = () => {
    const transformation = preset.transformations[0];
    if (transformation?.type === 'jewelry_photo_studio') {
      return <Gem className="w-5 h-5 text-purple-600" />;
    }
    if (transformation?.type === 'product_photo_studio') {
      return <Camera className="w-5 h-5 text-blue-600" />;
    }
    return <Home className="w-5 h-5 text-amber-600" />;
  };

  // Check if confirm button should be disabled
  const isConfirmDisabled = () => {
    const transformation = preset.transformations[0];

    if (transformation?.type === 'product_photo_studio' || transformation?.type === 'jewelry_photo_studio') {
      // For Product/Jewelry Photo Studio, require output size
      if (!inputValues.outputSize) return true;
      // If custom size selected, require both dimensions
      if (inputValues.outputSize === 'custom') {
        const width = parseInt(inputValues.customWidth);
        const height = parseInt(inputValues.customHeight);
        if (!width || !height || width < 100 || width > 4000 || height < 100 || height > 4000) {
          return true;
        }
      }
      return false;
    }

    // Default validation for object_placement presets
    return !inputValues.product || (!selectedEnvironment && !inputValues.environment);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getDialogIcon()}
            Configure {preset.name}
          </DialogTitle>
          <DialogDescription>
            {preset.description}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {preset.inputFields.map(field => {
            // Check conditional visibility
            if (!shouldShowField(field)) {
              return null;
            }

            return (
              <div key={field.name} className="space-y-2">
                <Label htmlFor={field.name} className="text-sm font-medium">
                  {field.label}
                </Label>
                {field.helpText && (
                  <p className="text-sm text-muted-foreground">{field.helpText}</p>
                )}

                {/* Checkbox input */}
                {field.type === 'checkbox' ? (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id={field.name}
                      checked={inputValues[field.name] === 'true'}
                      onCheckedChange={(checked: boolean) => handleInputChange(field.name, checked ? 'true' : 'false')}
                    />
                    <Label htmlFor={field.name} className="text-sm text-muted-foreground cursor-pointer">
                      {inputValues[field.name] === 'true' ? 'Enabled' : 'Disabled'}
                    </Label>
                  </div>
                ) : field.type === 'color' ? (
                  /* Color input */
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      id={field.name}
                      value={inputValues[field.name] || '#FFFFFF'}
                      onChange={(e) => handleInputChange(field.name, e.target.value)}
                      className="w-12 h-10 rounded border border-input cursor-pointer"
                    />
                    <Input
                      type="text"
                      value={inputValues[field.name] || '#FFFFFF'}
                      onChange={(e) => handleInputChange(field.name, e.target.value)}
                      placeholder="#FFFFFF"
                      className="w-28 font-mono"
                    />
                    <span className="text-sm text-muted-foreground">
                      {inputValues[field.name] === '#FFFFFF' ? 'White (Default)' : 'Custom Color'}
                    </span>
                  </div>
                ) : field.type === 'number' ? (
                  /* Number input */
                  <Input
                    id={field.name}
                    type="number"
                    min={100}
                    max={4000}
                    placeholder={field.placeholder}
                    value={inputValues[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                  />
                ) : field.type === 'select' ? (
                  field.options === 'environmentPresets' ? (
                    <>
                      <Select
                        value={selectedEnvironment}
                        onValueChange={(value) => handleInputChange(field.name, value)}
                      >
                        <SelectTrigger className="text-left">
                          <SelectValue placeholder={field.placeholder} />
                        </SelectTrigger>
                        <SelectContent className="max-h-[300px]">
                          {isLoadingPresets ? (
                            <div className="flex items-center justify-center p-4">
                              <Loader2 className="w-4 h-4 animate-spin" />
                            </div>
                          ) : (
                            <>
                              {Object.entries(environmentPresets).length === 0 ? (
                                <div className="p-4 text-center text-muted-foreground">
                                  No environment presets available
                                </div>
                              ) : (
                                Object.entries(environmentPresets).map(([category, presets]) => (
                                  <div key={category}>
                                    <div className="px-2 py-1 text-sm font-semibold text-muted-foreground">
                                      {category}
                                    </div>
                                    {presets.map(envPreset => (
                                      <SelectItem key={envPreset.id} value={envPreset.id}>
                                        <div className="flex flex-col">
                                          <span>{envPreset.name}</span>
                                          <span className="text-xs text-muted-foreground">
                                            {envPreset.description}
                                          </span>
                                        </div>
                                      </SelectItem>
                                    ))}
                                  </div>
                                ))
                              )}
                              <div className="border-t mt-2 pt-2">
                                <SelectItem value="custom">
                                  <div className="flex items-center gap-2">
                                    <Sparkles className="w-4 h-4" />
                                    <span>Custom Environment</span>
                                  </div>
                                </SelectItem>
                              </div>
                            </>
                          )}
                        </SelectContent>
                      </Select>

                      {selectedEnvironment === 'custom' && (
                        <Textarea
                          placeholder="Describe your custom environment in detail..."
                          value={customEnvironment}
                          onChange={(e) => setCustomEnvironment(e.target.value)}
                          className="mt-2"
                          rows={3}
                        />
                      )}
                    </>
                  ) : Array.isArray(field.options) ? (
                    // Handle select fields with array options
                    <Select
                      value={inputValues[field.name] || ''}
                      onValueChange={(value) => handleInputChange(field.name, value)}
                    >
                      <SelectTrigger className="text-left">
                        <SelectValue placeholder={field.placeholder || 'Select an option'} />
                      </SelectTrigger>
                      <SelectContent>
                        {field.options.map((option: any) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : null
                ) : field.type === 'textarea' ? (
                  <Textarea
                    id={field.name}
                    placeholder={field.placeholder}
                    value={inputValues[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                    rows={3}
                  />
                ) : (
                  <Input
                    id={field.name}
                    type={field.type || 'text'}
                    placeholder={field.placeholder}
                    value={inputValues[field.name] || ''}
                    onChange={(e) => handleInputChange(field.name, e.target.value)}
                  />
                )}
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isConfirmDisabled()}
          >
            Apply Transformation
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}